import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { BalanceMessage } from "@wr/contracts";
import { BALANCE_RECONCILE_DIM_DELAY_MS } from "../game/constants";
import { useSession } from "./SessionProvider";
import { useIdentity } from "../hooks/usePrivateKey";

/**
 * The live coin balance, app-wide — phase plan **S4-8**.
 *
 * ── Why it is a provider and not a hook on one screen ──────────────────────
 *
 * The coin chip is **chrome** (S2 §10): it is on screen for the whole of the
 * lobby, the whole of a room, and every settings/profile screen in between. The
 * events arrive over whichever socket the player currently holds, and those
 * sockets belong to two different hooks on two different screens. So the number
 * has to live above both, in the same place the chip does.
 *
 * ── Two sources, and which one wins ────────────────────────────────────────
 *
 *  1. **The authoritative read** — `players.me.balanceCoins`, from
 *     `SessionProvider`. It is the seed at boot and it is what a resubscribe
 *     reconciles against.
 *  2. **The socket** — one `balance` message per ledger write on this player's
 *     own channel, carrying the row's `balance_after`, **plus one snapshot the
 *     room sends on join and on reconnect** (§16g A76). The snapshot is what
 *     closes Q10: Redis pub/sub is not durable, so a client with no subscribed
 *     socket at the instant money moved misses that number permanently, and
 *     before A76 nothing inside a room ever said it again.
 *
 * The socket wins **while it is ahead**, and "ahead" is decided by
 * `transactionId` rather than by arrival order: a reconcile that lands after an
 * event it predates must not roll the chip back to the older number. That is the
 * one ordering bug this design can have, and it is the one a player would
 * notice — a prize that appears and then un-appears.
 *
 * ── LOCKED: `—` means unknown, `0` means zero ──────────────────────────────
 *
 * (Ivy §9.2.) A guest with a real, empty wallet sees `0`; a client with no
 * balance source sees `—`. This provider never substitutes one for the other,
 * which is why the seed is `number | null` all the way from the contract.
 */

export interface BalanceState {
  /** `null` = no claim is being made. Never a stand-in zero. */
  balanceCoins: number | null;
  /**
   * **Did the current number arrive as an event the player witnessed?** (Ivy
   * §9.4's liveness half, added in §20.4.)
   *
   * `true` only for a **published** socket event — the case the motion exists
   * for. `false` for the boot seed, for the **reconcile after a reconnect**, and
   * for a room's **balance snapshot** (§16g A76), all of which must land as an
   * instant swap: animating a catch-up would stage an outage's worth of movement
   * as a credit that just happened. The chip reads this rather than guessing
   * from the size of the change, because "did the player see this happen" is not
   * a property of the number.
   */
  witnessed: boolean;
  /** Apply one socket event. Older transactions are ignored. */
  applyBalanceMessage: (message: BalanceMessage) => void;
  /**
   * **The reconcile after a resubscribe.**
   *
   * One authoritative read, so a missed event cannot leave a stale number
   * rendered as live. It is deliberately not a "fetch the balance" route: the
   * session reload is the read that already exists, is per-caller, and is
   * uncached — and having exactly one of them is what keeps the chip and the
   * ledger from disagreeing.
   */
  reconcileBalance: () => Promise<void>;
  /**
   * **Q10 (Quinn, T-224) — a bright chip is a claim, and after settlement it has
   * to be earned.**
   *
   * On the **losing** side of a settled game the chip read `1,000` for 69/69
   * samples while the ledger said `975`, at full opacity, with nothing on screen
   * suggesting anything was unrefreshed. S2 §21.2 justified the brightness on the
   * grounds that *"the credit was published and animated seconds before
   * disposal"* — **and on the losing side no credit is ever published**, so the
   * premise was false exactly where the ruling leaned on it.
   *
   * Ivy's §14.3 replaces the premise with a mechanism: brightness after
   * settlement is asserted by an **authoritative read**, not by the room's
   * disposal. This flag is the other half — *"the two dims are independent facts,
   * and the chip is bright only if neither holds"*:
   *
   *  - **pending past `balanceReconcileDimDelayMs` (400 ms)** — no flash in the
   *    ordinary case, no confident wrong number in the slow one;
   *  - **failed** — the chip dims and **stays** dimmed, holding its last value,
   *    **never `—`**. That is a true statement: we do not know.
   */
  reconcileDim: boolean;
}

const BalanceContext = createContext<BalanceState | null>(null);

export function BalanceProvider({ children }: { children: ReactNode }) {
  const { profile, reload } = useSession();
  const identity = useIdentity();
  /** The last transaction this client has seen. `-1` = none yet. */
  const seenTransactionId = useRef(-1);
  const [live, setLive] = useState<number | null>(null);
  /**
   * Whether the number now rendered came off the socket. It is state rather than
   * a ref because the chip renders from it, and it is cleared by the reconcile —
   * see `reconcileBalance`.
   */
  const [witnessed, setWitnessed] = useState(false);
  /** §14.3's second dim. `idle` is *"nothing is being reconciled"*, not *"verified"*. */
  const [reconcile, setReconcile] = useState<"idle" | "pending" | "failed">("idle");
  const [slow, setSlow] = useState(false);

  /**
   * **SEC-2 (T-910) — an identity reset, separate from A130's precedence rule.**
   *
   * The provider never remounts, so without this a number the socket delivered
   * to account A is still `live` after A logs out — and `live` outranks the
   * session read, so it is what account B's chip shows. When the identity
   * generation changes, everything this provider holds about the previous
   * account goes: `live`, `witnessed`, the reconcile state and its dim.
   *
   * **During render, not in an effect** (React's "adjusting state when a prop
   * changes" pattern): an effect runs after the commit, so the first render
   * under the new account would already have painted the old number. Setting
   * state here makes React discard this render and re-run it before anything
   * below it renders. The watermark is a ref and is reset by the effect below
   * — nothing renders from it, and a socket event cannot arrive between a
   * render and its commit.
   *
   * The guest conversion keeps its generation (see `SessionProvider`), so this
   * never fires for it. A130's `setLive(null)` in `reconcileBalance` is a
   * different rule — precedence hand-off to a read — and is untouched.
   */
  const [liveFor, setLiveFor] = useState(identity);
  if (liveFor !== identity) {
    setLiveFor(identity);
    setLive(null);
    setWitnessed(false);
    setReconcile("idle");
    setSlow(false);
  }
  useEffect(() => {
    seenTransactionId.current = -1;
  }, [identity]);
  /** For `reconcileBalance`'s continuation (T-910 arm 4). */
  const identityRef = useRef(identity);
  identityRef.current = identity;

  const applyBalanceMessage = useCallback((message: BalanceMessage) => {
    /**
     * Out-of-order and duplicate delivery are both real: Redis pub/sub gives no
     * ordering guarantee across publishers, and a room may resend on resubscribe.
     * `transactionId` is monotonic per the `coin_transactions` sequence, so it is
     * the only ordering this client can trust — and ignoring an older one is what
     * stops a refund event arriving late from undoing a prize.
     *
     * **A room's snapshot (§16g A76) is ordered by the same number** — it
     * carries the id of the last transaction its balance includes — so a
     * snapshot that arrives after a newer event is dropped here, unchanged, and
     * needs no special case. A snapshot equal to the watermark is dropped too,
     * which is correct: it is the number already on screen.
     */
    if (message.transactionId <= seenTransactionId.current) return;
    seenTransactionId.current = message.transactionId;
    setLive(message.balanceCoins);
    /**
     * **Did the player watch this arrive?**
     *
     * A published event is a change on a live connection: the player is looking
     * at the screen it lands on, so §9.4's motion applies. **A snapshot is not**
     * — A76 is explicit that the client adopts it *"under Ivy §9.4's reconcile
     * rules: instant swap, no cross-fade, no rise, no coin-dot pulse, because it
     * is a value the player did not witness arriving."* It is the same
     * distinction `reconcileBalance` already draws, arriving over the socket
     * instead of over HTTP, and it is a property of the message rather than of
     * the size of the change.
     *
     * A76's closing line, recorded so it is not lost: this means the **entry
     * debit lands on the chip without motion** on any path where the snapshot is
     * what delivers it. If Ivy wants that one animated, the snapshot needs a
     * `witnessed` flag on the join case, which is her call and not a defect.
     */
    setWitnessed(message.snapshot !== true);
  }, []);

  const reconcileBalance = useCallback(async () => {
    /**
     * The read is authoritative, so it also **resets the ordering watermark**:
     * whatever the server just said is the truth as of now, and an event this
     * client missed while it was disconnected has an id it will never see.
     * Keeping the old watermark would make the first post-reconnect event look
     * like a duplicate and be dropped.
     */
    seenTransactionId.current = -1;
    /**
     * **The reconciled value lands as an instant swap** (Ivy §9.4 / §20.4): it is
     * a catch-up, not a credit the player watched arrive, and it can move the
     * number by an outage's worth of money in one frame. Cleared *before* the
     * read so the arriving value is never briefly treated as witnessed.
     */
    setWitnessed(false);
    setReconcile("pending");
    const reconcilingFor = identityRef.current;
    /**
     * `reload()` does not reject — a transport failure is reported rather than
     * thrown (`SessionProvider.load`'s catch) — so the boolean is what *"we
     * checked"* means here. **The read wins any disagreement and the change lands
     * silently** (§14.3 part 2): the ledger is the authority (S4 §8.3's one-truth
     * rule), so there is nothing to animate and nothing to narrate.
     */
    const ok = await reload();
    /**
     * SEC-2 (T-910 arm 4): the account changed while the read was in flight.
     * The identity reset above already cleared this provider for the new
     * account; this read's verdict is about the old one and writes nothing.
     */
    if (identityRef.current !== reconcilingFor) return;
    /**
     * **A130 — `setLive(null)` is precedence hand-off, and it is paid at the
     * RESOLUTION of the read rather than at its start.**
     *
     * Its only job is to let the read win: `balanceCoins` is
     * `live ?? profile?.balanceCoins`, so unless `live` is cleared a resolved
     * read can never reach the chip and §14.3 part 2's *"the read wins any
     * disagreement"* would be false. That purpose is unchanged. What was wrong
     * was the timing — clearing up front opened a window, exactly as long as the
     * read (staging: median ≈ 242 ms, under the 400 ms dim delay, so bright),
     * in which the chip rendered `profile.balanceCoins`: **the one value in the
     * system no source is asserting.** The socket has superseded it by
     * `transactionId`; the in-flight read is about to supersede it by recency.
     * That is this file's own named nightmare in the header above — *a prize
     * that appears and then un-appears* — and it was firing at every call site.
     *
     * Two guards, both load-bearing:
     *
     *  1. **`ok`** — a read that did not answer may not demote the chip.
     *     `reload()` reports `false` on a transport failure **and** on the
     *     §14.1 r2 `converting` stand-down (*"we did not ask"* is not *"we
     *     checked"*). §14.3's failed arm is *"dims and **stays** dimmed, holding
     *     its last value"*; clearing here would drop it to the older session
     *     number and dim **that** instead.
     *  2. **`seenTransactionId.current === -1`** — *"no socket event has been
     *     applied since this reconcile reset the watermark."* Without it an
     *     event landing mid-flight would be discarded by the resolution, which
     *     is the same ordering bug pointed the other way. Two overlapping
     *     reconciles are safe under it: both clear, or neither.
     *
     * **A strict narrowing:** when `live` is already `null` — no socket event
     * since the last authoritative read — the behaviour is byte-identical to
     * before. And because the fall-back to the session number now happens only
     * where there is nothing newer, the header's guardrail sentence is finally
     * true as written: the chip keeps the last value **it** had, and never
     * flashes `—` (§20.4 guardrail 1).
     *
     * **Known residual (A130 §1.5, accepted):** an event arriving mid-flight
     * that is *older* than the read's snapshot pins this guard open, so the
     * read's number is masked until the next event. Root cause: `players/me`
     * returns a balance with **no transaction id**, so the two sources cannot be
     * ordered against each other. The durable fix is `BALANCE-READ-WATERMARK`
     * (a wire change, deferred); its re-open trigger is an observed instance or
     * the next balance-mutating surface that ships without a socket.
     */
    if (ok && seenTransactionId.current === -1) setLive(null);
    setReconcile(ok ? "idle" : "failed");
  }, [reload]);

  /**
   * §14.3 part 3 — *"the in-flight window is not bright-and-unverified for longer
   * than 400 ms. Hold the last value; if the read has not resolved by then, dim to
   * 60% until it does."*
   */
  useEffect(() => {
    if (reconcile !== "pending") {
      setSlow(false);
      return;
    }
    const timer = setTimeout(() => setSlow(true), BALANCE_RECONCILE_DIM_DELAY_MS);
    return () => clearTimeout(timer);
  }, [reconcile]);

  const value = useMemo<BalanceState>(
    () => ({
      // The socket's number while it has one; the session's read otherwise.
      balanceCoins: live ?? profile?.balanceCoins ?? null,
      witnessed,
      applyBalanceMessage,
      reconcileBalance,
      reconcileDim: reconcile === "failed" || (reconcile === "pending" && slow),
    }),
    [
      applyBalanceMessage,
      live,
      profile?.balanceCoins,
      reconcile,
      reconcileBalance,
      slow,
      witnessed,
    ],
  );

  return <BalanceContext.Provider value={value}>{children}</BalanceContext.Provider>;
}

export function useBalance(): BalanceState {
  const value = useContext(BalanceContext);
  if (!value) throw new Error("useBalance must be used inside <BalanceProvider>");
  return value;
}
